import { FlightDetails, ConnectionFlight } from "../types";

// Parse an "HH:MM:SS", "HH:MM", or "Xh Ym" duration string to total minutes.
export const durationToMinutes = (raw?: string): number => {
  if (!raw) return 0;
  if (raw.includes("h") || raw.includes("m")) {
    const hMatch = raw.match(/(\d+)\s*h/);
    const mMatch = raw.match(/(\d+)\s*m/);
    const h = hMatch ? parseInt(hMatch[1], 10) : 0;
    const m = mMatch ? parseInt(mMatch[1], 10) : 0;
    return h * 60 + m;
  }
  const parts = raw.split(":");
  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  return h * 60 + m;
};

// Format a minute count as "20h 55m" (matches the segment/layover style on
// the search-results page). Handles totals over 24h.
export const formatMinutes = (total: number): string => {
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (hours && minutes) return `${hours}h ${minutes}m`;
  if (hours) return `${hours}h`;
  return `${minutes}m`;
};

// Compute a connection's total (gate-to-gate) duration in minutes.
export const getConnectionTotalDurationMinutes = (
  connection: ConnectionFlight,
): number => {
  const segmentMinutes = connection.segments.reduce(
    (sum, segment) => sum + durationToMinutes(segment.duration),
    0,
  );
  const layoverMinutes =
    (connection.layoverMinutes || 0) + (connection.secondLayoverMinutes || 0);
  return segmentMinutes + layoverMinutes;
};

// Compute a connection's total (gate-to-gate) duration string from its segments and
// layover. We do NOT use connection.totalDuration because the backend formats
// it as a 24h clock time, so any trip over 24h wraps (e.g. 26h45m -> 2h45m).
export const getConnectionTotalDuration = (
  connection: ConnectionFlight,
): string => {
  return formatMinutes(getConnectionTotalDurationMinutes(connection));
};

export const getFlightDurationMinutes = (flight: FlightDetails): number => {
  if (typeof flight.liveDurationMinutes === "number" && flight.liveDurationMinutes > 0) {
    return flight.liveDurationMinutes;
  }
  return durationToMinutes(flight.duration || flight.elptim);
};

export const parseDepartureTimestamp = (rawTime?: string): number => {
  if (!rawTime) return 0;
  const t = new Date(rawTime).getTime();
  if (!isNaN(t)) return t;
  const parts = rawTime.split(":");
  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  return h * 60 + m;
};

export const sortFlightDetails = (
  flights: FlightDetails[],
  sortBy?: string | null,
  sortOrder?: string | null,
): FlightDetails[] => {
  if (!sortOrder) return flights;
  const key = sortBy || "duration";
  const asc = sortOrder === "asc";

  return flights.slice().sort((a, b) => {
    let valA = 0;
    let valB = 0;

    if (key === "departureTime" || key === "departure") {
      valA = parseDepartureTimestamp(a.departureTime);
      valB = parseDepartureTimestamp(b.departureTime);
    } else if (key === "arrival") {
      valA = parseDepartureTimestamp(a.arrivalTime);
      valB = parseDepartureTimestamp(b.arrivalTime);
    } else if (key === "stops") {
      valA = a.stops ?? 0;
      valB = b.stops ?? 0;
    } else {
      valA = getFlightDurationMinutes(a);
      valB = getFlightDurationMinutes(b);
    }

    if (valA !== valB) {
      return asc ? valA - valB : valB - valA;
    }
    // Retain default ordering when tie-breaking equal values
    return 0;
  });
};

export const sortConnectionFlights = (
  connections: ConnectionFlight[],
  sortBy?: string | null,
  sortOrder?: string | null,
): ConnectionFlight[] => {
  if (!sortOrder) return connections;
  const key = sortBy || "duration";
  const asc = sortOrder === "asc";

  return connections.slice().sort((a, b) => {
    let valA = 0;
    let valB = 0;

    if (key === "departureTime" || key === "departure") {
      valA = parseDepartureTimestamp(
        a.overallDepartureTime || a.segments[0]?.departureTime
      );
      valB = parseDepartureTimestamp(
        b.overallDepartureTime || b.segments[0]?.departureTime
      );
    } else if (key === "arrival") {
      valA = parseDepartureTimestamp(
        a.overallArrivalTime || a.segments[a.segments.length - 1]?.arrivalTime
      );
      valB = parseDepartureTimestamp(
        b.overallArrivalTime || b.segments[b.segments.length - 1]?.arrivalTime
      );
    } else if (key === "layover") {
      valA = (a.layoverMinutes || 0) + (a.secondLayoverMinutes || 0);
      valB = (b.layoverMinutes || 0) + (b.secondLayoverMinutes || 0);
    } else if (key === "stops") {
      valA = a.segments.length - 1;
      valB = b.segments.length - 1;
    } else {
      valA = getConnectionTotalDurationMinutes(a);
      valB = getConnectionTotalDurationMinutes(b);
    }

    if (valA !== valB) {
      return asc ? valA - valB : valB - valA;
    }
    return 0;
  });
};
